import { Helmet } from 'react-helmet-async';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Layout } from '../components/layout';
import { Leaf, Recycle, Factory, TrendingUp, MapPin, Users, Award, Target } from 'lucide-react';
import { useSafeAnimation } from '../components/ui/SafeMotion';

const SITE_URL = 'https://www.theecoenviro.com';

const stats = [
  { value: '100,000+', label: 'MT Waste Processed', icon: Recycle },
  { value: '95%', label: 'Landfill Diversion', icon: Target },
  { value: '30+', label: 'Clients Served', icon: Users },
  { value: '7', label: 'States Coverage', icon: MapPin },
];

const impactPoints = [
  {
    icon: Recycle,
    title: 'Plastic Diverted from Dumps',
    description: 'Thousands of tons of plastic waste recovered and channeled to authorized recyclers instead of landfills.',
  },
  {
    icon: Leaf,
    title: 'Paper Recycled into New Products',
    description: 'OCC, cartons, and paper waste transformed into recycled paper products, saving trees and water.',
  },
  {
    icon: Factory,
    title: 'RDF Replacing Fossil Fuels',
    description: 'Low-value plastics converted to refuse-derived fuel for cement kilns, reducing coal dependency.',
  },
  {
    icon: TrendingUp,
    title: 'Metals Returned to Manufacturing',
    description: 'Ferrous and non-ferrous metals recovered and sent back into the manufacturing cycle.',
  },
];

export function Impact() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  // SSR-safe animation props
  const heroAnim = useSafeAnimation(
    { opacity: 0, y: 30 },
    { opacity: 1, y: 0 }
  );
  const statAnim = useSafeAnimation(
    { opacity: 0, y: 30 },
    { opacity: 1, y: 0 },
    isInView
  );
  const sectionAnim = useSafeAnimation(
    { opacity: 0, y: 20 },
    { opacity: 1, y: 0 }
  );
  const certAnim = useSafeAnimation(
    { opacity: 0, scale: 0.9 },
    { opacity: 1, scale: 1 }
  );

  return (
    <Layout>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>Our Impact | Eco Enviro - Environmental Impact & Sustainability Results</title>
        <meta name="title" content="Our Impact | Eco Enviro - Environmental Impact & Sustainability Results" />
        <meta name="description" content="Eco Enviro's environmental impact: 100,000+ MT waste processed, 95% landfill diversion rate, serving 30+ clients across 7 states in India. CPCB authorized recycler." />
        <meta name="keywords" content="Eco Enviro impact, waste diversion India, landfill diversion, environmental impact, sustainability results, CPCB authorized recycler" />
        <meta name="robots" content="index, follow" />

        {/* Canonical URL */}
        <link rel="canonical" href={`${SITE_URL}/impact`} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/impact`} />
        <meta property="og:title" content="Our Impact | Eco Enviro - Environmental Results" />
        <meta property="og:description" content="100,000+ MT waste processed, 95% landfill diversion, 30+ clients across 7 states. CPCB authorized recycler." />
        <meta property="og:site_name" content="Eco Enviro Circular Solutions" />
        <meta property="og:image" content={`${SITE_URL}/images/og-image.png`} />
        <meta property="og:locale" content="en_IN" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@theecoenviro" />
        <meta name="twitter:title" content="Our Impact | Eco Enviro - Environmental Results" />
        <meta name="twitter:description" content="100,000+ MT waste processed, 95% landfill diversion, 30+ clients across 7 states." />
        <meta name="twitter:image" content={`${SITE_URL}/images/og-image.png`} />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-eco-green-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={heroAnim.initial}
            animate={heroAnim.animate}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="inline-block px-4 py-2 bg-eco-green-100 text-eco-green-700 text-sm font-semibold rounded-full mb-4">
              Our Impact
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-eco-green-900 mb-6">
              Measured Environmental Impact
            </h1>
            <p className="text-xl text-gray-600">
              Every ton recovered reduces landfill, saves resources, and lowers emissions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section ref={ref} className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={statAnim.initial}
                animate={statAnim.animate}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-eco-green-50 rounded-2xl p-6 text-center"
              >
                <div className="inline-flex items-center justify-center w-14 h-14 bg-eco-green-600 rounded-xl mb-4">
                  <stat.icon className="w-7 h-7 text-white" />
                </div>
                <p className="text-3xl sm:text-4xl font-bold text-eco-green-700 mb-2">{stat.value}</p>
                <p className="text-gray-600 text-sm">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Points */}
      <section className="py-16 bg-eco-green-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            initial={sectionAnim.initial}
            whileInView={sectionAnim.animate}
            viewport={{ once: true }}
            className="text-3xl font-bold text-eco-green-900 text-center mb-12"
          >
            How We Create Impact
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            {impactPoints.map((point, index) => (
              <motion.div
                key={point.title}
                initial={sectionAnim.initial}
                whileInView={sectionAnim.animate}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 flex gap-4"
              >
                <div className="bg-eco-green-100 p-3 rounded-xl h-fit">
                  <point.icon className="w-6 h-6 text-eco-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-eco-green-900 mb-2">{point.title}</h3>
                  <p className="text-gray-600 text-sm">{point.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={sectionAnim.initial}
            whileInView={sectionAnim.animate}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-eco-green-900 mb-8">Certifications & Compliance</h2>
            <div className="flex flex-wrap justify-center gap-6">
              {[
                { icon: Award, title: 'CPCB Authorized', subtitle: 'Registered Recycler' },
                { icon: Award, title: 'Certified', subtitle: 'Quality Management' },
                { icon: Award, title: 'EPR Compliant', subtitle: 'Extended Producer Responsibility' },
              ].map((cert, index) => (
                <motion.div
                  key={cert.title}
                  initial={certAnim.initial}
                  whileInView={certAnim.animate}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-eco-green-50 rounded-xl p-5 flex items-center gap-4"
                >
                  <div className="bg-eco-green-600 p-3 rounded-xl">
                    <cert.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-eco-green-900">{cert.title}</p>
                    <p className="text-sm text-eco-green-700">{cert.subtitle}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
